import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';

import { RoutesDictionaryByCode } from '@legacy/constants';
import { RouteCodeEnum } from '@models/menu';
import { Permission } from '@models/permission';
import { buildEmailsCommands } from '@module-instance/inbox/common/correspondence-routes.helper';
import { SYSTEM_FOLDERS } from '@module-instance/inbox/common/models/inbox-list.models';
import { NgToJsService } from '@ng2/services';
import { MsalAuthService } from '@shared/services';
import { AiStore, TenantStore, UserStore } from '@signal-store/index';
import { AccountMenuComponent } from '@system/account/account-menu.component';
import { ContextInfo, ContextTree } from '@system/context';
import { ContextViewComponent } from '@system/context/view/context-view.component';
import {
  SearchOverlayContainerComponent,
  SearchOverlayContentType,
} from '@system/search/overlay/search-overlay.component';

@Component({
  selector: 'app-header',
  imports: [
    FlexLayoutModule,
    MatBadgeModule,
    MatToolbarModule,
    ContextViewComponent,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    TranslateModule,
    AccountMenuComponent,
    SearchOverlayContainerComponent,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements AfterViewInit, OnDestroy {
  @ViewChild(SearchOverlayContainerComponent) searchOverlay: SearchOverlayContainerComponent;
  @ViewChild(ContextViewComponent, { read: ElementRef, static: true }) contextView: ElementRef;

  authService = inject(MsalAuthService);
  angularJsService = inject(NgToJsService);
  tenantStore = inject(TenantStore);
  userStore = inject(UserStore);
  router = inject(Router);
  aiStore = inject(AiStore);

  isSearchToggled = signal<boolean>(false);
  isCipoAiToggled = computed(() => this.aiStore.isOpen());

  canOpenContract = computed(() => {
    const localContext = this.localContext();
    if (!localContext) {
      return false;
    }
    return this.userStore
      .getModuleByCode(RouteCodeEnum.Contracts)
      ?.operations?.find(op => op.id === Permission.Read && (localContext.contract?.canEdit ?? false));
  });
  correspondenceModule = computed(() => this.userStore.getModuleByCode(RouteCodeEnum.Correspondence));
  canOpenCorrespondence = computed(() =>
    //   TODO: remove this check, because the user should always have the permission to read correspondence if it's in the menu
    this.correspondenceModule()?.operations?.find(op => op.id === Permission.Read),
  );
  localContext = computed(() => this.userStore.userData()?.context);

  private resizeObserver: ResizeObserver;

  constructor() {
    effect(() => {
      const localContext = this.localContext();
      if (!localContext) {
        return;
      }
      this.angularJsService.changeContext(this.localContext(), false);
    });
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  ngAfterViewInit(): void {
    this.onContextSizeChanges();
  }

  onToggleChange(isToggled: boolean): void {
    this.isSearchToggled.set(isToggled && this.searchOverlay.currentContentType === 'search');
  }

  public toggleSearchOverlay(type: SearchOverlayContentType) {
    if (type === 'cipo-ai') {
      this.aiStore.toggleOpen();
      return;
    }
    this.searchOverlay.toggleOverlay(type);
  }

  logoClick() {
    this.userStore.setActiveMenuByCode(RouteCodeEnum.Dashboard);
    this.router.navigate([this.tenantStore.selectedTenantName(), RoutesDictionaryByCode[RouteCodeEnum.Dashboard]]);
  }

  openContracts() {
    this.angularJsService.editContract();
  }

  contextChanged(context: ContextInfo) {
    this.angularJsService.changeContext(context, true);
    this.userStore.updateContext(context);
  }

  goToCorrespondence() {
    this.userStore.setActiveMenuByCode(RouteCodeEnum.Correspondence);
    const contractId = this.userStore.contract()?.id;
    if (!contractId) {
      return;
    }
    this.router.navigate([
      this.tenantStore.selectedTenantName(),
      ...buildEmailsCommands(contractId, SYSTEM_FOLDERS.INBOX),
    ]);
  }

  contextDataLoaded(contextTree: ContextTree) {
    this.angularJsService.contextDataLoaded(contextTree);
  }

  /** Method to listen for changes in the size of the context view and update overlay position accordingly
   */
  private onContextSizeChanges() {
    if (this.contextView) {
      const contextElement = this.contextView.nativeElement;

      this.resizeObserver = new ResizeObserver(() => {
        const rect = contextElement.getBoundingClientRect();

        this.searchOverlay.updateOverlayPosition(rect);
      });

      this.resizeObserver.observe(contextElement);
    }
  }

  private getEntityInstanceId(context: ContextInfo): number {
    return (
      context?.contract?.entityInstanceId ?? context?.project?.entityInstanceId ?? context?.program?.entityInstanceId
    );
  }
}
